import { useState, useEffect } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { supabase } from '../../../lib/supabase';

interface Review {
  id: number;
  name: string;
  role: string | null;
  text: string;
  rating: number;
}

const Stars = ({ count }: { count: number }) => (
  <div className="flex gap-0.5">
    {[1, 2, 3, 4, 5].map((s) => (
      <i
        key={s}
        className={`${s <= count ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-sm`}
      />
    ))}
  </div>
);

const TestimonialsSection = () => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loadingReviews, setLoadingReviews] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const PREVIEW_COUNT = 4;

  // Form state
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [text, setText] = useState('');
  const [rating, setRating] = useState(5);
  const [hoverRating, setHoverRating] = useState(0);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const header = useScrollAnimation();
  const grid = useScrollAnimation({ threshold: 0.1 });
  const formAnim = useScrollAnimation({ threshold: 0.1 });

  const fetchReviews = async () => {
    const { data, error } = await supabase
      .from('reviews')
      .select('id, name, role, text, rating')
      .eq('status', 'approved')
      .order('created_at', { ascending: false });

    if (!error && data) {
      setReviews(data as Review[]);
    }
    setLoadingReviews(false);
  };

  useEffect(() => {
    fetchReviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !text.trim() || rating === 0) return;

    setSubmitStatus('loading');

    const { error: dbError } = await supabase.from('reviews').insert({
      name: name.trim(),
      email: email.trim(),
      role: role.trim() || null,
      text: text.trim(),
      rating,
      status: 'pending',
    });

    if (dbError) {
      setSubmitStatus('error');
      return;
    }

    // Notifica email via edge function
    try {
      await supabase.functions.invoke('send-review-notification', {
        body: { name, email, role, text, rating },
      });
    } catch (err) {
      console.error('Email notification failed:', err);
    }

    setSubmitStatus('success');
    setName('');
    setEmail('');
    setRole('');
    setText('');
    setRating(5);
    setHoverRating(0);

    setTimeout(() => setSubmitStatus('idle'), 5000);
  };

  const staggerClass = (i: number) => {
    const n = Math.min(i + 1, 6);
    return `stagger-${n}`;
  };

  return (
    <section id="recensioni" className="bg-ivory-100 py-24 md:py-32 px-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div
          ref={header.ref}
          className={`text-center mb-16 md:mb-20 fade-in-up ${header.isVisible ? 'visible' : ''}`}
        >
          <p className="font-sans text-xs tracking-widest2 uppercase text-terracotta-300 mb-5">
            Testimonianze
          </p>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-anthracite-800 font-light leading-tight max-w-2xl mx-auto">
            Cosa dicono di <em className="cormorant-italic not-italic text-terracotta-300">noi</em>
          </h2>
          <p className="font-sans text-sm md:text-base text-anthracite-400 leading-relaxed mt-5 max-w-xl mx-auto">
            Storie reali di chi ha scelto di vivere, investire e innamorarsi dei borghi delle Marche insieme a noi.
          </p>
        </div>

        {/* Reviews grid wrapper — always mounted so ref is attached */}
        <div
          ref={grid.ref}
          className={`fade-in ${grid.isVisible ? 'visible' : ''}`}
        >
          {!loadingReviews && reviews.length > 0 && (
            <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {(showAll ? reviews : reviews.slice(0, PREVIEW_COUNT)).map((t, i) => (
                <div
                  key={t.id}
                  className={`bg-white rounded-xl p-8 border border-transparent hover:border-terracotta-100 transition-all duration-400 flex flex-col ${staggerClass(i)}`}
                >
                <Stars count={t.rating} />
                <div className="mt-5 mb-4">
                  <i className="ri-double-quotes-l text-2xl text-terracotta-200" />
                </div>
                <p className="font-sans text-sm md:text-base text-anthracite-600 leading-relaxed flex-1">
                  {t.text}
                </p>
                <div className="mt-6 mb-5 h-px bg-gradient-to-r from-transparent via-gold-200 to-transparent" />
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-terracotta-100 flex items-center justify-center shrink-0">
                    <span className="font-serif text-sm text-terracotta-500 font-medium">
                      {t.name.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <p className="font-sans text-sm font-medium text-anthracite-800">{t.name}</p>
                    {t.role && (
                      <p className="font-sans text-xs text-anthracite-400">{t.role}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            </div>

            {/* Show more / collapse button */}
            {reviews.length > PREVIEW_COUNT && (
              <div className="mt-10 text-center">
                <button
                  onClick={() => setShowAll((prev) => !prev)}
                  className="inline-flex items-center gap-2 font-sans text-sm tracking-widest uppercase px-8 py-4 border border-terracotta-300 text-terracotta-300 hover:bg-terracotta-300 hover:text-white rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  {showAll ? (
                    <><i className="ri-arrow-up-line"></i> Mostra meno</>
                  ) : (
                    <><i className="ri-arrow-down-line"></i> Vedi tutte le recensioni ({reviews.length})</>
                  )}
                </button>
              </div>
            )}
            </>
          )}

        {!loadingReviews && reviews.length === 0 && (
          <div className="text-center py-12">
            <p className="font-sans text-anthracite-400 text-sm">
              Nessuna recensione ancora. Sii il primo a lasciare la tua!
            </p>
          </div>
        )}
        </div>

        {/* Decorative divider */}
        <div className="mt-20 flex items-center gap-6 justify-center">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-gold-200 max-w-32" />
          <i className="ri-heart-3-line text-terracotta-300 text-xl" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-gold-200 max-w-32" />
        </div>

        {/* Form */}
        <div
          ref={formAnim.ref}
          className={`mt-20 max-w-2xl mx-auto fade-in-up ${formAnim.isVisible ? 'visible' : ''}`}
        >
          <div className="bg-white rounded-xl p-8 md:p-10 border border-terracotta-100">
            <h3 className="font-serif text-2xl md:text-3xl text-anthracite-800 font-light mb-2">
              Lascia la tua <em className="cormorant-italic not-italic text-terracotta-300">esperienza</em>
            </h3>
            <p className="font-sans text-sm text-anthracite-400 mb-8">
              La tua recensione verr&agrave; pubblicata dopo una breve verifica.
            </p>

            {submitStatus === 'success' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-green-100">
                  <i className="ri-check-line text-green-600 text-lg" />
                </div>
                <p className="font-sans text-sm font-medium text-green-800">
                  Grazie per la tua recensione!
                </p>
                <p className="font-sans text-xs text-green-600 mt-1">
                  Verr&agrave; pubblicata dopo una breve approvazione.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block font-sans text-xs tracking-widest uppercase text-anthracite-500 mb-2">
                      Nome *
                    </label>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="w-full font-sans text-sm px-4 py-3 rounded-md border border-anthracite-200 bg-white text-anthracite-800 placeholder-anthracite-300 focus:outline-none focus:border-terracotta-300 transition-colors"
                      placeholder="Il tuo nome"
                    />
                  </div>
                  <div>
                    <label className="block font-sans text-xs tracking-widest uppercase text-anthracite-500 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="w-full font-sans text-sm px-4 py-3 rounded-md border border-anthracite-200 bg-white text-anthracite-800 placeholder-anthracite-300 focus:outline-none focus:border-terracotta-300 transition-colors"
                      placeholder="La tua email"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-xs tracking-widest uppercase text-anthracite-500 mb-2">
                    Contesto
                  </label>
                  <input
                    type="text"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="w-full font-sans text-sm px-4 py-3 rounded-md border border-anthracite-200 bg-white text-anthracite-800 placeholder-anthracite-300 focus:outline-none focus:border-terracotta-300 transition-colors"
                    placeholder="Es. Da Milano, ho comprato a Osimo"
                  />
                </div>

                <div>
                  <label className="block font-sans text-xs tracking-widest uppercase text-anthracite-500 mb-2">
                    Valutazione *
                  </label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setRating(s)}
                        onMouseEnter={() => setHoverRating(s)}
                        onMouseLeave={() => setHoverRating(0)}
                        className="w-8 h-8 flex items-center justify-center cursor-pointer"
                      >
                        <i
                          className={`${s <= (hoverRating || rating) ? 'ri-star-fill' : 'ri-star-line'} text-amber-400 text-xl`}
                        />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block font-sans text-xs tracking-widest uppercase text-anthracite-500 mb-2">
                    Recensione *
                  </label>
                  <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full font-sans text-sm px-4 py-3 rounded-md border border-anthracite-200 bg-white text-anthracite-800 placeholder-anthracite-300 focus:outline-none focus:border-terracotta-300 transition-colors resize-none"
                    placeholder="Racconta la tua esperienza con Discovering Marche..."
                  />
                  <p className="font-sans text-xs text-anthracite-300 mt-1 text-right">
                    {text.length}/500
                  </p>
                </div>

                {submitStatus === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2">
                    <i className="ri-error-warning-line text-red-500" />
                    <p className="font-sans text-xs text-red-600">
                      Si &egrave; verificato un errore. Riprova pi&ugrave; tardi.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={submitStatus === 'loading'}
                  className="w-full sm:w-auto font-sans text-sm tracking-widest uppercase px-10 py-4 bg-terracotta-300 text-white hover:bg-terracotta-400 transition-all duration-300 hover:scale-105 cursor-pointer whitespace-nowrap rounded-full disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  {submitStatus === 'loading' ? 'Invio in corso...' : 'Invia recensione'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;